import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { supabase } from "@/integrations/supabase/client";

// Measures how long a signed-in partner actually spends on a partner platform,
// and on which tool.
//
// ── WHY HEARTBEATS AND NOT A STOPWATCH ─────────────────────────────────────
// The obvious design is "note when the page opens, note when it closes,
// subtract". It does not survive contact with reality: `beforeunload` does not
// fire when a phone tab is killed, when the lid shuts, or when the browser is
// force-quit. Sessions then either vanish or run forever — and the second kind
// is worse, because one orphaned session reads as eleven hours of engagement
// and quietly poisons every average built on top of it.
//
// So: one small row every PING_SECONDS while the tab is VISIBLE. Time is
// `ping count × PING_SECONDS`. Nothing has to close cleanly, and the worst a
// dropped ping costs is fifteen seconds.
//
// ── VISIBLE, NOT OPEN ──────────────────────────────────────────────────────
// A tab left open behind another window is not use, and counting it would make
// every number a flattering lie. The interval is torn down on
// `visibilitychange` and rebuilt when the tab comes back. A laptop that sleeps
// stops pinging on its own, because timers do not fire while suspended.
//
// ── TWO TABS CANNOT DOUBLE YOUR TIME ───────────────────────────────────────
// The database holds UNIQUE (user_id, bucket) where `bucket` is the timestamp
// floored to the interval, and inserts are ON CONFLICT DO NOTHING. A second tab
// pinging in the same window collides and is discarded, so total time is
// impossible to inflate. See migration 20260924000001 for the trade that makes
// — total exact, per-tool approximate at the margin.
//
// ── FIRE AND FORGET ────────────────────────────────────────────────────────
// Nothing here is awaited by anything the user can see, and every failure is
// swallowed. Measurement must never be able to break the product it measures.

/** Seconds between heartbeats. MUST match `v_ping_seconds` in
 *  `rpc_partner_usage` — the report multiplies by it to get minutes, so the two
 *  numbers are one decision written in two places. */
const PING_SECONDS = 15;

/** A per-tab identifier, so sessions can be counted. Regenerated on reload,
 *  which is the correct granularity: a reload IS a new visit. */
function tabSessionId(): string {
  const KEY = "rb_partner_session_id";
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(KEY, id);
    return id;
  } catch {
    // Private mode, or storage disabled. A per-mount id still counts sessions
    // approximately, and approximate is better than dropping the ping.
    return `nostore-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * The tool the person is looking at, derived from the path.
 *
 * Partner tool routes are `/<partner>/<tool-key>` (e.g. `/foote/weight-map`),
 * so the key is simply the second segment — which means adding a tool to
 * TOOL_CATALOG needs no change here. `null` means the partner home page, which
 * the report labels "(home)".
 */
export function toolKeyFromPath(pathname: string, partnerSlug: string): string | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== partnerSlug) {
    // A partner-skinned page outside the /<partner>/ prefix (the directories,
    // for instance). Record the first segment so it is still attributable.
    return parts[0] ?? null;
  }
  return parts[1] ?? null;
}

/**
 * Sends heartbeats while a signed-in partner has this tab visible.
 *
 * Mount ONCE, inside the router. Does nothing at all when `partnerSlug` is
 * null, or when nobody is signed in.
 */
export function usePartnerHeartbeat(partnerSlug: string | null): void {
  const { pathname } = useLocation();
  // Held in a ref so the interval always sends the CURRENT path without being
  // torn down and rebuilt on every navigation — which would restart the clock
  // and systematically under-count anyone who clicks around.
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    if (!partnerSlug || typeof window === "undefined") return;

    let timer: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;
    const sessionId = tabSessionId();

    const ping = async () => {
      if (cancelled || document.visibilityState !== "visible") return;
      try {
        const { data } = await supabase.auth.getSession();
        const userId = data.session?.user?.id;
        if (!userId) return; // signed out — nothing to attribute

        // ⚠️ NO TIMESTAMP IS SENT, DELIBERATELY. `pinged_at` and `bucket` are
        // stamped by a BEFORE INSERT trigger from server time, and anything a
        // client sends is discarded. Until 2026-09-24 the browser supplied both,
        // which let a signed-in user POST 240 backdated rows and manufacture an
        // hour of usage — and, needing no ill intent at all, let a wrong system
        // clock file real time under the wrong day.
        const path = pathRef.current;
        await supabase.from("partner_usage_pings").insert({
          partner_slug: partnerSlug,
          user_id: userId,
          session_id: sessionId,
          path,
          tool_key: toolKeyFromPath(path, partnerSlug),
        });
      } catch {
        // Deliberately silent. A measurement failure is not a product failure,
        // and a console full of insert errors trains people to ignore the
        // console — which is where the real ones appear.
      }
    };

    const start = () => {
      if (timer) return;
      void ping(); // count the first interval immediately, not 15s late
      timer = setInterval(() => void ping(), PING_SECONDS * 1000);
    };

    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    const onVisibility = () => (document.visibilityState === "visible" ? start() : stop());

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // partnerSlug only. `pathname` deliberately absent — see pathRef above.
  }, [partnerSlug]);
}
